import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { api, clearToken, getToken, useHeartbeat } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Terms from "@/pages/Terms";
import ResetPassword from "@/pages/ResetPassword";
import Viewer from "@/pages/Viewer";
import Profile from "@/pages/Profile";
import AdminDashboard from "@/pages/AdminDashboard";
import { termsExpired } from "@/lib/time";

// Admin never sees T&C. Franchise partners must re-accept every 15 days.
function needsTerms(user) {
  if (!user || user.role === "admin") return false;
  return termsExpired();
}

function Shell() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) { setLoading(false); return; }
    api.get("/auth/me").then(r => setUser(r.data)).catch(() => setUser(null)).finally(() => setLoading(false));
  }, []);

  useHeartbeat();

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* offline — still clear */ }
    clearToken(); setUser(null);
    // Preserve the 15-day terms cache across logouts (they signed once).
    nav("/login");
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#B4841F] font-mono2 text-xs tracking-[0.3em] uppercase">Loading vault…</div>;

  const home = user ? (user.role === "admin" ? "/admin" : "/viewer") : "/login";
  const gate = user ? (needsTerms(user) ? "/terms" : home) : "/login";

  return (
    <Routes>
      <Route path="/terms" element={user ? (user.role === "admin" ? <Navigate to="/admin" /> : <Terms />) : <Navigate to="/login" />} />
      <Route path="/login" element={user ? <Navigate to={needsTerms(user) ? "/terms" : home} /> : <Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/viewer" element={user ? (needsTerms(user) ? <Navigate to="/terms" /> : <Viewer user={user} onLogout={logout} />) : <Navigate to="/login" />} />
      <Route path="/profile" element={user ? <Profile user={user} onLogout={logout} /> : <Navigate to="/login" />} />
      <Route path="/admin" element={user?.role === "admin" ? <AdminDashboard user={user} onLogout={logout} /> : <Navigate to="/login" />} />
      <Route path="*" element={<Navigate to={gate} />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
      <Toaster theme="light" position="top-center" richColors />
    </div>
  );
}
